import { isEqualWith, isObject } from 'lodash'
import { Frequency, RRule } from 'rrule'
import { format } from 'date-fns'
import { DateTime } from 'luxon'
import {
  EventRecurrenceDayEnum,
  EventRecurrenceFreqEnum,
  EventRecurrenceMonthEnum
} from './enums/recurringEvent'
import { Options } from 'rrule/dist/esm/types'
import { utcToZonedTime } from 'date-fns-tz'

const EXCLUDE_KEYS = ['count']

export interface IEventRecurrencePattern {
  count: number
  freq?: EventRecurrenceFreqEnum
  interval?: number
  byDay?: EventRecurrenceDayEnum[] | null
  byMonth?: EventRecurrenceMonthEnum[] | null
  byYear?: number | null
}

/**
 * compare two recurrence DTOs, returns true if there are some changes
 * the most commons use to check do we need to clear future events, future events must be delete if pattern was changed somehow expect `count` property
 */
export function isRecurrencePatternChanged(
  currentPattern: IEventRecurrencePattern,
  newPattern: IEventRecurrencePattern
) {
  if (!isObject(currentPattern) || !isObject(newPattern)) {
    throw new Error('Patterns must be an objects')
  }
  const currentPatternKeys = Object.keys(currentPattern)
  const newPatternKeys = Object.keys(newPattern)

  if (currentPatternKeys.length !== newPatternKeys.length) {
    return true
  }
  /**
   * checkTS don't like unused a,b vars :)
   */
  // @ts-expect-error
  return !isEqualWith(currentPattern, newPattern, (a, b, key: string) => {
    if (EXCLUDE_KEYS.includes(key)) return true
  })
}

export function haveDatesBeenChanged(
  previousDates: {
    start_time: Date | null
    end_time: Date | null
  },
  newDates: {
    start_time: Date | null
    end_time: Date | null
  }
) {
  return (
    previousDates.start_time?.getTime() === newDates.start_time?.getTime() &&
    previousDates.end_time?.getTime() === newDates.end_time?.getTime()
  )
}

/**
 * check equality of two dates where is one can be shifted by daylight saving time, which can be 30 minutes or 1 hour
 * this is more like a hack for MS back sync
 * TODO get rid of luxon?
 */
export function checkDateOnDTS(date: Date, dateWithPossibleDTS: Date) {
  const dateLuxoned = DateTime.fromJSDate(date)
  const dateWithPossibleDTSLuxoned = DateTime.fromJSDate(dateWithPossibleDTS)

  const dateWith30MinutesShift = dateWithPossibleDTSLuxoned.plus({
    minutes: 30
  })
  const dateWith1HourShift = dateWithPossibleDTSLuxoned.plus({ minutes: 60 })
  const dateWith1HourMinusShift = dateWithPossibleDTSLuxoned.minus({
    minutes: 60
  })

  return (
    dateLuxoned.toString() === dateWith30MinutesShift.toString() ||
    dateLuxoned.toString() === dateWith1HourShift.toString() ||
    dateLuxoned.toString() === dateWith1HourMinusShift.toString() ||
    dateLuxoned.toString() === dateWithPossibleDTSLuxoned.toString()
  )
}

const FREQUENCY_RULES_MAP = {
  [EventRecurrenceFreqEnum.daily]: Frequency.DAILY,
  [EventRecurrenceFreqEnum.weekly]: Frequency.WEEKLY,
  [EventRecurrenceFreqEnum.monthly]: Frequency.MONTHLY,
  [EventRecurrenceFreqEnum.yearly]: Frequency.YEARLY
}

const FREQUENCY_RULES_MAP_FOR_MS = {
  [EventRecurrenceFreqEnum.daily]: 'daily',
  [EventRecurrenceFreqEnum.weekly]: 'weekly',
  [EventRecurrenceFreqEnum.monthly]: 'relativeMonthly',
  [EventRecurrenceFreqEnum.yearly]: 'absoluteYearly'
}

export function parseFreqEnumToRruleEnum(freq: EventRecurrenceFreqEnum) {
  const result = FREQUENCY_RULES_MAP[freq]

  if (!result) throw new Error('Unsupported frequency setting')

  return result
}

export function parseFreqEnumToMSEnum(freq: EventRecurrenceFreqEnum) {
  const result = FREQUENCY_RULES_MAP_FOR_MS[freq]

  if (!result) throw new Error('Unsupported frequency setting')

  return result
}

const DAY_RULES_MAP = {
  [EventRecurrenceDayEnum.mo]: RRule.MO,
  [EventRecurrenceDayEnum.tu]: RRule.TU,
  [EventRecurrenceDayEnum.we]: RRule.WE,
  [EventRecurrenceDayEnum.th]: RRule.TH,
  [EventRecurrenceDayEnum.fr]: RRule.FR,
  [EventRecurrenceDayEnum.sa]: RRule.SA,
  [EventRecurrenceDayEnum.su]: RRule.SU
}

const DAY_RULES_MAP_FOR_MS = {
  [EventRecurrenceDayEnum.mo]: 'Monday',
  [EventRecurrenceDayEnum.tu]: 'Tuesday',
  [EventRecurrenceDayEnum.we]: 'Wednesday',
  [EventRecurrenceDayEnum.th]: 'Thursday',
  [EventRecurrenceDayEnum.fr]: 'Friday',
  [EventRecurrenceDayEnum.sa]: 'Saturday',
  [EventRecurrenceDayEnum.su]: 'Sunday'
}

// O_O  https://docs.microsoft.com/en-gb/graph/api/resources/recurrencepattern?view=graph-rest-1.0
const WEEK_ORDER_FOR_MS = {
  [1]: 'first',
  [2]: 'second',
  [3]: 'third',
  [4]: 'fourth',
  [5]: 'last'
}

export function parseWeekDayEnumToRruleEnum(weekDay: EventRecurrenceDayEnum) {
  const result = DAY_RULES_MAP[weekDay]

  if (!result) throw new Error('Invalid day of week')

  return result
}
export function parseWeekDayEnumToMSEnum(weekDay: EventRecurrenceDayEnum) {
  const result = DAY_RULES_MAP_FOR_MS[weekDay]

  if (!result) throw new Error('Invalid day of week')

  return result
}

export function validateWeekDaysArray(weekDays: EventRecurrenceDayEnum[]) {
  if (!Array.isArray(weekDays)) {
    throw new Error('Weekdays should be an array of weekdays')
  }
  if (weekDays.length > 7) {
    throw new Error('Invalid week days array')
  }
  if (new Set(weekDays).size !== weekDays.length) {
    throw new Error('Array of weekdays should consist only uniq values')
  }
}

export function parseArrayOfWeekDaysEnumToRruleEnums(
  weekDays: EventRecurrenceDayEnum[]
) {
  validateWeekDaysArray(weekDays)
  return weekDays.map((wd) => parseWeekDayEnumToRruleEnum(wd))
}

export function parseArrayOfWeekDaysEnumToMSEnums(
  weekDays: EventRecurrenceDayEnum[]
) {
  validateWeekDaysArray(weekDays)
  return weekDays.map((wd) => parseWeekDayEnumToMSEnum(wd))
}

export function createNewRRuleInstanceForPatternAndDate(
  startTime: Date,
  pattern: IEventRecurrencePattern,
  timeZone?: string | null
) {
  if (!startTime) {
    throw new Error('Start time for recurrence was not provided')
  }
  if (!pattern.freq) {
    throw new Error('Frequency recurrence was not provided')
  }

  const {
    count,
    freq,
    interval,
    byDay,
    byMonth: bymonth,
    byYear: byeaster
  } = pattern

  let options: Partial<Options> = {
    count,
    freq: parseFreqEnumToRruleEnum(freq),
    interval: interval,
    bymonth,
    byeaster,
    dtstart: startTime
  }

  if (timeZone) {
    options.tzid = timeZone
  }

  if (byDay && byDay.length > 0) {
    options.byweekday = parseArrayOfWeekDaysEnumToRruleEnums(byDay)
  }

  Object.keys(options).forEach(
    (key) => options[key] === undefined && delete options[key]
  )
  if (freq === EventRecurrenceFreqEnum.monthly) {
    options.bysetpos = Math.ceil(startTime.getDate() / 7)
    const dayOfWeek = format(
      startTime,
      'EEEEEE'
    ).toUpperCase() as EventRecurrenceDayEnum
    options.byweekday = parseArrayOfWeekDaysEnumToRruleEnums([dayOfWeek])
  }

  return new RRule(options)
}

export function getDatesForRecurringPatternAndDate(
  startTimeUTC: Date,
  pattern: IEventRecurrencePattern,
  timezone?: string | null
) {
  const rule = createNewRRuleInstanceForPatternAndDate(
    // If timezone is provided, we need to get startTimeUTC in the time of the timezone, not in UTC.
    timezone ? utcToZonedTime(startTimeUTC, timezone) : startTimeUTC,
    pattern,
    timezone
  )
  return rule.all()
}

export function getRRuleStringForPatternAndDate(
  startTimeUTC: Date,
  pattern: IEventRecurrencePattern,
  timeZone?: string | null
) {
  const rule = createNewRRuleInstanceForPatternAndDate(
    // If timezone is provided, we need to get startTimeUTC in the time of the timezone, not in UTC.
    timeZone ? utcToZonedTime(startTimeUTC, timeZone) : startTimeUTC,
    pattern,
    timeZone
  )
  const rRuleString = rule.toString().split('\n')
  return {
    dtstart: rRuleString[0],
    rrule: rRuleString[1]
  }
}

export function getHumanizedRepeatPattern(
  startTime: Date,
  pattern: IEventRecurrencePattern
) {
  const rule = createNewRRuleInstanceForPatternAndDate(startTime, pattern)
  return rule.toText()
}

export function mapRecurrenceToMSFormat(startTime, pattern) {
  const startDate = format(startTime, 'yyyy-MM-dd')
  const { freq, byDay, byMonth: month } = pattern
  const msFreq = parseFreqEnumToMSEnum(freq)
  let daysOfWeek
  let index
  if (pattern.freq === EventRecurrenceFreqEnum.weekly) {
    daysOfWeek = parseArrayOfWeekDaysEnumToMSEnums(byDay)
  }
  if (pattern.freq === EventRecurrenceFreqEnum.monthly) {
    const dayOfWeek = format(
      startTime,
      'EEEEEE'
    ).toUpperCase() as EventRecurrenceDayEnum
    daysOfWeek = parseArrayOfWeekDaysEnumToMSEnums([dayOfWeek])
    const numOfWeekInMonth = Math.ceil(startTime.getDate() / 7)
    index = WEEK_ORDER_FOR_MS[numOfWeekInMonth]
    if (numOfWeekInMonth > 5) {
      index = 'last'
    }
  }
  return {
    pattern: {
      type: msFreq,
      interval: pattern.interval,
      daysOfWeek,
      month,
      index
    },
    range: {
      type: 'numbered',
      numberOfOccurrences: pattern.count,
      startDate
    }
  }
}

export function mapRecurrencePatternForIcal(startTime, pattern) {
  const { count, freq, interval, byDay, byMonth } = pattern

  let options = {
    count,
    freq,
    interval: interval,
    byMonth,
    byDay
  }

  Object.keys(options).forEach(
    (key) => options[key] === undefined && delete options[key]
  )
  if (freq === EventRecurrenceFreqEnum.monthly) {
    options['bysetpos'] = Math.ceil(startTime.getDate() / 7)
    const dayOfWeek = format(
      startTime,
      'EEEEEE'
    ).toUpperCase() as EventRecurrenceDayEnum
    options.byDay = [dayOfWeek]
  }

  return options
}
